import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import FormHelperText from '@mui/material/FormHelperText';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CustomCircularProgress from '../customCircularProgress/customCircularProgress.component';
import CustomAutocomplete, { OptionRecord } from '../customAutocomplete/customAutocomplete.component';
import { useCRUD } from '../../../api/crud';
import {
  ERROR_SNACKBAR,
  JOB_FORM_RULES_ENDPOINT,
  JOB_SERVICES_ENDPOINT,
  SUCCESS_SNACKBAR,
} from '../../../consts';
import { JobFormRuleFieldType } from '../../../types/jobs';
import { JOB_FORM_RULE_FIELDS, JOB_FORM_RULE_FIELD_TYPES } from '../../service/jobFormRules';

type Props = {
  id?: string | number;
  onSuccess: () => void;
};

const extractError = (errors: Record<string, unknown>, key: string): string => {
  const val = errors[key];
  if (!val) return '';
  if (typeof val === 'string') return val;
  if (Array.isArray(val) && val.length > 0) {
    const first = val[0];
    if (typeof first === 'string') return first;
    if (first && typeof first === 'object' && 'message' in first)
      return (first as { message?: string }).message ?? '';
  }
  return '';
};

const TASK_FIELD_TYPES = JOB_FORM_RULE_FIELD_TYPES.filter((type) => JOB_FORM_RULE_FIELDS[type].onTask);
const JOB_FIELD_TYPES = JOB_FORM_RULE_FIELD_TYPES.filter((type) => !JOB_FORM_RULE_FIELDS[type].onTask);

type FieldOption = { id: JobFormRuleFieldType; label: string };

/** Adds or edits a job form rule: the services that trigger it and the fields it requires. */
const JobFormRuleForm: React.FC<Props> = ({ id, onSuccess }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { loading, get, create, updatePUT } = useCRUD(JOB_FORM_RULES_ENDPOINT);

  const [initialLoading, setInitialLoading] = useState(!!id);
  const [services, setServices] = useState<OptionRecord[]>([]);
  const [fieldTypes, setFieldTypes] = useState<Set<JobFormRuleFieldType>>(new Set());
  const [errors, setErrors] = useState<Record<string, unknown>>({});

  useEffect(() => {
    if (!id) return;
    get(id).then((result) => {
      if (result?.success && result.data) {
        setServices(result.data.services ?? []);
        setFieldTypes(
          new Set(
            (result.data.required_fields ?? []).map(
              (field: { field_type: JobFormRuleFieldType }) => field.field_type
            )
          )
        );
      }
      setInitialLoading(false);
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const toFieldOptions = (types: JobFormRuleFieldType[]): FieldOption[] =>
    types.map((type) => ({ id: type, label: t(JOB_FORM_RULE_FIELDS[type].labelKey) }));

  // Each select edits its own group of fields and leaves the other group's alone.
  const handleFieldsChange = (groupTypes: JobFormRuleFieldType[], selected: FieldOption[]) =>
    setFieldTypes(
      (prev) =>
        new Set([
          ...Array.from(prev).filter((type) => !groupTypes.includes(type)),
          ...selected.map((option) => option.id),
        ])
    );

  const handleSubmit = async () => {
    setErrors({});
    const payload = {
      services: services.map((service) => service.id),
      // In the screens' order, whatever order they were ticked in.
      required_fields: JOB_FORM_RULE_FIELD_TYPES.filter((type) => fieldTypes.has(type)).map(
        (field_type) => ({ field_type })
      ),
    };

    const result = id ? await updatePUT({ id, ...payload }) : await create(payload);

    if (result?.success) {
      enqueueSnackbar(
        t(id ? 'Job form rule saved successfully' : 'Job form rule created successfully'),
        SUCCESS_SNACKBAR
      );
      onSuccess();
    } else if (result?.errorData) {
      setErrors(result.errorData);
      enqueueSnackbar(t('Please fix the errors and try again.'), ERROR_SNACKBAR);
    } else {
      enqueueSnackbar(t('An unexpected error occurred'), ERROR_SNACKBAR);
    }
  };

  if (initialLoading) return <CustomCircularProgress />;

  const topLevelError = extractError(errors, 'non_field_errors') || extractError(errors, 'detail');
  const servicesError = extractError(errors, 'services');
  const fieldsError = extractError(errors, 'required_fields');

  const renderFieldSelect = (types: JobFormRuleFieldType[], label: string) => (
    <CustomAutocomplete
      multiple
      filterSelectedOptions
      options={toFieldOptions(types)}
      value={toFieldOptions(types.filter((type) => fieldTypes.has(type)))}
      onChange={(_, newValue) => handleFieldsChange(types, newValue as FieldOption[])}
      getOptionLabel={(option) => option.label}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      renderInput={(params) => (
        <TextField {...params} label={label} error={Boolean(fieldsError)} />
      )}
    />
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
      {topLevelError && <Alert severity="error">{topLevelError}</Alert>}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Typography variant="subtitle1" fontWeight={600}>
          {t('When a task has any of these services')}
        </Typography>
        <CustomAutocomplete
          multiple
          filterSelectedOptions
          apiConfig={{ url: JOB_SERVICES_ENDPOINT, queryParams: { is_active: 'true' } }}
          value={services}
          onChange={(_, newValue) => setServices(newValue as OptionRecord[])}
          getOptionLabel={(option) => option.title}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('Services')}
              error={Boolean(servicesError)}
              helperText={servicesError}
            />
          )}
        />
      </Box>

      <Divider />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Typography variant="subtitle1" fontWeight={600}>
          {t('Required fields')}
        </Typography>
        {renderFieldSelect(TASK_FIELD_TYPES, t('Require on that task'))}
        {renderFieldSelect(JOB_FIELD_TYPES, t('Require on the job'))}
        {fieldsError && <FormHelperText error>{fieldsError}</FormHelperText>}
      </Box>

      <Button variant="contained" onClick={handleSubmit} disabled={loading}>
        {loading ? t('Processing...') : t('Submit')}
      </Button>
    </Box>
  );
};

export default JobFormRuleForm;
